import {
  EExportFileType,
  EExportType,
  exportFileModule,
} from "@rentiohq/shared-frontend/dist/reduxV2/exportFile";
import {
  EScheduledExportFrequency,
  LAST_DAY_OF_MONTH,
  scheduledExportModule,
} from "@rentiohq/shared-frontend/dist/reduxV2/scheduledExport";
import { IRootStore } from "redux/reducers";
import {
  EField,
  FREQUENCY_ONCE,
  LAST_DAY_OPTION,
} from "./schema.createCustomExport.types";

// The form creates either a one-off export or a schedule. Only the module the last submit went
// to may report its progress: createStart resets that module's create state, never the other's,
// so reading both would let an earlier one-off export's success (or a schedule's error) answer
// for the submit that is running now.
let submittedToSchedule = false;

// Called when the modal opens, so a schedule created the previous time it was open cannot answer
// for this one.
export const resetSubmitTarget = () => {
  submittedToSchedule = false;
};

export const wasScheduleSubmitted = () => submittedToSchedule;

const getModule = () =>
  submittedToSchedule ? scheduledExportModule : exportFileModule;

export const isPerformingSubmitActionSelector = (state: IRootStore) =>
  getModule().selectors.isCreating(state);

export const performingSubmitActionErrorSelector = (state: IRootStore) =>
  getModule().selectors.createError(state);

export const performingSubmitActionResultSelector = (state: IRootStore) =>
  getModule().selectors.getCreatedDetail(state);

// A form value as a whole number, or null when it is empty or not a number.
const toInteger = (value: unknown) => {
  const number = Number(value);

  return value !== null && value !== "" && Number.isInteger(number)
    ? number
    : null;
};

// A weekly schedule runs on a weekday, a monthly or yearly one on a day of the month.
const getRunDay = (
  frequency: EScheduledExportFrequency,
  formData: { [key: string]: unknown },
) => {
  switch (frequency) {
    case EScheduledExportFrequency.Weekly:
      return toInteger(formData[EField.RunWeekday]);

    case EScheduledExportFrequency.Monthly:
    case EScheduledExportFrequency.Yearly:
      return formData[EField.RunDayOfMonth] === LAST_DAY_OPTION
        ? LAST_DAY_OF_MONTH
        : toInteger(formData[EField.RunDayOfMonth]);

    default:
      return null;
  }
};

export const submitActionCreator =
  () => (formData: { [key: string]: unknown }) => {
    const exportType = formData[EField.ExportType] as EExportType;
    const exportFileType = formData[EField.ExportFileType] as EExportFileType;
    const frequency = formData[EField.Frequency] as
      | EScheduledExportFrequency
      | typeof FREQUENCY_ONCE;

    if (frequency === FREQUENCY_ONCE) {
      submittedToSchedule = false;

      return exportFileModule.actions.createStart.getAction({
        data: { exportType, exportFileType, exportParameters: {} },
      });
    }

    submittedToSchedule = true;

    return scheduledExportModule.actions.createStart.getAction({
      data: {
        exportType,
        exportFileType,
        frequency,
        runDay: getRunDay(frequency, formData),
        runMonth:
          frequency === EScheduledExportFrequency.Yearly
            ? toInteger(formData[EField.RunMonth])
            : null,
      },
    });
  };
